import React, { useMemo } from "react";
import { Marker, useMap } from "react-leaflet";
import L from "leaflet";
import { createDonutIcon } from "./DonutMarkerIcon";
import {
    CLUSTER_RISE_OFFSET,
    getClusterZIndexOffsets,
    getDonutSize,
} from "../../utils/clusterMarkerLayout";
import { worldCopyShifts } from "../../utils/mapBounds";
import {
    ClusterData,
    MERGE_MAX_ZOOM,
    ProjectedItem,
    mergeOverlappingClusters,
} from "../../utils/mergeClusters";

export type { ClusterData };

interface ClusterMarkersProps {
    clusters: ClusterData[];
    colorMap: Record<string, string>;
    /** UI language for the count label's decimal separator. */
    locale?: string;
    onSingleLocationClick?: (center: ClusterData["center"]) => void;
}

const ClusterMarkers: React.FC<ClusterMarkersProps> = ({
    clusters,
    colorMap,
    locale = "en",
    onSingleLocationClick,
}) => {
    const map = useMap();

    // Everything below is derived once per cluster result. Every pan/zoom refetches the
    // clusters (moveend/zoomend → debounced query), so the map state read here is the
    // state the data was fetched for; listening to zoomend as well would only rebuild
    // every marker's DOM once more per zoom step.
    const layout = useMemo(() => {
        const zoom = map.getZoom();
        const bounds = map.getBounds();

        // INTERIM client-side merge of overlapping donuts until the backend clusters by
        // pixel radius per zoom (docs/map-clustering-backend-handoff.md). Skipped at high
        // zoom so single locations stay individually clickable.
        let displayClusters = clusters;
        if (zoom < MERGE_MAX_ZOOM) {
            const projected: ProjectedItem<ClusterData>[] = clusters.map((cluster) => {
                const point = map.project([cluster.center.lat, cluster.center.lng], zoom);
                return {
                    x: point.x,
                    y: point.y,
                    radius: getDonutSize(cluster.totalCount) / 2,
                    count: cluster.totalCount,
                    item: cluster,
                };
            });
            displayClusters = mergeOverlappingClusters(projected);
        }

        return {
            displayClusters,
            // Bigger clusters stack above smaller ones (Leaflet's default is "further south wins").
            zIndexOffsets: getClusterZIndexOffsets(displayClusters.map((c) => c.totalCount)),
            icons: displayClusters.map((c) =>
                createDonutIcon(c.assetStatusCounts, c.totalCount, colorMap, locale)
            ),
            // The basemap repeats horizontally (fill zoom, continent-centred overviews across the
            // antimeridian); repeat the markers on the visible copies.
            lngShifts: worldCopyShifts(bounds.getWest(), bounds.getEast()),
        };
    }, [clusters, colorMap, locale, map]);

    // `lngShift` is the world copy the clicked marker sits on, so the map zooms in there
    // instead of flying back to the primary world.
    const handleClusterClick = (cluster: ClusterData, lngShift: number) => {
        if (cluster.singleLocation && onSingleLocationClick) {
            onSingleLocationClick(cluster.center);
            return;
        }
        const { boundingBox } = cluster;
        const bounds = L.latLngBounds(
            [boundingBox.southwest.lat, boundingBox.southwest.lng + lngShift],
            [boundingBox.northeast.lat, boundingBox.northeast.lng + lngShift]
        );
        map.flyToBounds(bounds, { padding: [50, 50], maxZoom: 18 });
    };

    return (
        <>
            {layout.displayClusters.map((cluster, index) =>
                layout.lngShifts.map((shift) => (
                    <Marker
                        key={`${cluster.center.lat}-${cluster.center.lng}-${index}-${shift}`}
                        position={[cluster.center.lat, cluster.center.lng + shift]}
                        icon={layout.icons[index]}
                        zIndexOffset={layout.zIndexOffsets[index]}
                        riseOnHover
                        riseOffset={CLUSTER_RISE_OFFSET}
                        eventHandlers={{
                            click: () => handleClusterClick(cluster, shift),
                        }}
                    />
                ))
            )}
        </>
    );
};

export default ClusterMarkers;
